import { IPassportGroupForm } from '../interfaces/IPassportGroupForm';
import { IPassportGroupCreateRequest } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupCreateRequest';
import { IGroupConditionForm } from '../interfaces/IGroupConditionForm';
import { IPassportGroupCondition } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupCondition';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { SYSTEM_DATE_FORMAT_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { PassportGroupTypeEnum } from '../../../APIPackage/PassportGroupsModule/enums/PassportGroupTypeEnum';

const passportGroupConditionFormToRequest = (condition: IGroupConditionForm): IPassportGroupCondition => {
  return {
    name: condition.name,
    category: condition.category || null,
    commonConditions: {
      startDate: condition.commonConditions?.startDate
        ? formatDate({ date: condition.commonConditions.startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS })
        : null,
      endDate: condition.commonConditions?.endDate
        ? formatDate({ date: condition.commonConditions.endDate, dateFormat: SYSTEM_DATE_FORMAT_FNS })
        : null,
      domainIds: condition.commonConditions?.domains?.length
        ? condition.commonConditions?.domains?.map(item => item.id)
        : null,
      includeDomainChild:
        typeof condition.commonConditions?.includeDomainChild === 'boolean'
          ? condition.commonConditions.includeDomainChild
          : null,
      knoOrganizationIds: condition.commonConditions?.knoOrganizations?.length
        ? condition.commonConditions.knoOrganizations.map(item => item.value.dictVersionId)
        : null,
      includeKnoOrganizationChild:
        typeof condition.commonConditions?.includeKnoOrganizationChild === 'boolean'
          ? condition.commonConditions.includeKnoOrganizationChild
          : null,
      knoOrganizationAdminLevelIds: condition.commonConditions?.knoOrganizationLevels?.length
        ? condition.commonConditions.knoOrganizationLevels.map(item => item.id)
        : null,
      kindControlIds: condition.commonConditions?.kindControls?.length
        ? condition.commonConditions.kindControls.map(item => item.value.dictVersionId)
        : null,
      knmTypeIds: condition.commonConditions?.knmTypes?.length
        ? condition.commonConditions.knmTypes.map(item => item.id)
        : null,
      plans: condition.commonConditions?.plans?.length ? condition.commonConditions.plans.map(item => item.id) : null,
      knmKinds: condition.commonConditions?.knmKinds?.length
        ? condition.commonConditions.knmKinds.map(item => item.value.dictVersionId)
        : null,
      pmKinds: condition.commonConditions?.pmKinds?.length
        ? condition.commonConditions.pmKinds.map(item => item.value.dictVersionId)
        : null,
      decisionKinds: condition.commonConditions?.decisionKinds?.length
        ? condition.commonConditions.decisionKinds.map(item => item.value.dictVersionId)
        : null,
      reasons: condition.commonConditions?.reasons?.length
        ? condition.commonConditions.reasons.map(item => item.value.dictVersionId)
        : null,
      riskIndikators: condition.commonConditions?.riskIndikators?.length
        ? condition.commonConditions.riskIndikators.map(item => item.value.dictVersionId)
        : null,
      erpIds: condition.commonConditions?.erpIds?.length
        ? condition.commonConditions.erpIds.map(item => item.id.toString())
        : null,
      isUnableToRefuse: condition.commonConditions?.unableToRefuse
        ? condition.commonConditions.unableToRefuse.value
        : null,
    },
    assignmentConditions: {
      assigmentStartDate: condition.assignmentConditions?.assigmentStartDate
        ? formatDate({ date: condition.assignmentConditions.assigmentStartDate, dateFormat: SYSTEM_DATE_FORMAT_FNS })
        : null,
      assigmentEndDate: condition.assignmentConditions?.assigmentEndDate
        ? formatDate({ date: condition.assignmentConditions.assigmentEndDate, dateFormat: SYSTEM_DATE_FORMAT_FNS })
        : null,
      assignmentNumbers: condition.assignmentConditions?.assignmentNumbers?.length
        ? condition.assignmentConditions.assignmentNumbers.map(item => item.value as string)
        : null,
    },
    subjectConditions: {
      subjectTypes: condition.subjectConditions?.subjectTypes?.length
        ? condition.subjectConditions.subjectTypes.map(item => item.code || '')
        : null,
      regTypes: condition.subjectConditions?.regTypes?.length
        ? condition.subjectConditions.regTypes.map(item => item.id)
        : null,
      mspCategories: condition.subjectConditions?.mspCategories?.length
        ? condition.subjectConditions.mspCategories.map(item => item.code)
        : null,
      checkResultStartDate: condition.subjectConditions?.checkResultStartDate
        ? formatDate({ date: condition.subjectConditions.checkResultStartDate, dateFormat: SYSTEM_DATE_FORMAT_FNS })
        : null,
      checkResultEndDate: condition.subjectConditions?.checkResultEndDate
        ? formatDate({ date: condition.subjectConditions.checkResultEndDate, dateFormat: SYSTEM_DATE_FORMAT_FNS })
        : null,
    },
    objectConditions: {
      ervkNumbers: condition.objectConditions?.ervkNumbers?.length
        ? condition.objectConditions.ervkNumbers.map(item => item.value as string)
        : [],
      objectTypes: condition.objectConditions?.objectTypes?.length
        ? condition.objectConditions.objectTypes.map(item => item.value.dictVersionId)
        : [],
      objectKinds: condition.objectConditions?.objectKinds?.length
        ? condition.objectConditions.objectKinds.map(item => item.value.dictVersionId)
        : [],
      objectSubKinds: condition.objectConditions?.objectSubKinds?.length
        ? condition.objectConditions.objectSubKinds.map(item => item.value.dictVersionId)
        : [],
      riskCategories: condition.objectConditions?.riskCategories?.length
        ? condition.objectConditions.riskCategories.map(item => item.value.dictVersionId)
        : [],
      dangerClasses: condition.objectConditions?.dangerClasses?.length
        ? condition.objectConditions.dangerClasses.map(item => item.value.dictVersionId)
        : [],
    },
    eventSubjectConditions: {
      requirements: condition.eventSubjectConditions?.requirements?.length
        ? condition.eventSubjectConditions.requirements.map(item => ({
            npaId: item.npa?.id || 0,
            structuralUnits:
              item.structuralUnits?.map(suItem => ({
                structuralUnitId: suItem.structuralUnit.id,
                mandatoryIds: suItem.mandatory?.map(mItem => mItem.id) || null,
              })) || null,
          }))
        : null,
      docTargets: condition.eventSubjectConditions?.docTargets?.length
        ? condition.eventSubjectConditions.docTargets.map(item => item.targetId)
        : null,
    },
    eventConditions: {
      eventTypes: condition.eventConditions?.eventTypes?.length
        ? condition.eventConditions.eventTypes.map(item => item.value.dictVersionId)
        : null,
    },
    statusConditions: {
      statusIds: condition.statusConditions?.statuses?.length
        ? condition.statusConditions.statuses.map(item => item.id)
        : null,
      publishedStatus: condition.statusConditions?.publishedStatus?.value || null,
    },
  };
};

export const passportGroupFormToRequest = (
  formData: IPassportGroupForm,
  groupType: PassportGroupTypeEnum,
): IPassportGroupCreateRequest => {
  const include: IPassportGroupCreateRequest['filter']['include'] =
    formData.includes?.map(item => passportGroupConditionFormToRequest(item))?.[0] || null;
  const exclude: IPassportGroupCreateRequest['filter']['exclude'] =
    formData.exceptions?.map(item => passportGroupConditionFormToRequest(item))?.[0] || null;

  return {
    groupType,
    filter: {
      include,
      exclude,
    },
  };
};
